import he from 'he';
import omit from 'lodash/omit';
import Head from 'next/head';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  GalleryCategoriesContainer,
  GalleryHeaderContainer,
  GalleryListContainer,
  GalleryOverlay,
} from '@/components/route/listing/Gallery';
import { PAGE_TITLE_SUFFIX } from '@/constants/pages';
import { useRouter } from '@/hooks/useRouter';
import {
  getListingAlternatePaths,
  getListingCanonicalURL,
  getListingDescription,
  getListingId,
  getListingMetaProperties,
  getListingTitle,
} from '@/redux/selectors/listing/page';
import { trackEvent } from '@/services/track-event';
import { getParams } from '@/utility/queryParams';
import { createListingUrlObj } from '@/utility/routes';

type TListingPhotosIndexProps = {
  isCampsite?: boolean;
};

const ListingPhotosIndex: React.FC<TListingPhotosIndexProps> = ({ isCampsite }) => {
  const title = useSelector(getListingTitle);
  const description = useSelector(getListingDescription);
  const alternate = useSelector(getListingAlternatePaths);
  const canonicalURL = useSelector(getListingCanonicalURL);
  const metaProperties = useSelector(getListingMetaProperties);
  const rentalId = useSelector(getListingId);
  const router = useRouter();

  const goToListing = useCallback(() => {
    let urlPrefix = '/rv-rental/';
    if (isCampsite) urlPrefix = '/campsite-rental/';

    const query = omit(getParams(router, true), 'image', 'tab');
    router.push(
      createListingUrlObj(query, (location, slug) => `${urlPrefix}${location}/${slug}`),
      undefined,
      { shallow: true },
    );
  }, [router, isCampsite]);

  useEffect(() => {
    if (rentalId) {
      trackEvent({
        event: 'Listing : Show all photos',
        rental_id: rentalId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <title>
          {title ? `Photos | ${he.decode(title)}${PAGE_TITLE_SUFFIX}` : 'Photos | Outdoorsy'}
        </title>
        <meta name="description" content={description} />

        {metaProperties
          .filter(tag => tag.content)
          .map(({ property, content }) => (
            <meta key={property} property={property} content={content} />
          ))}

        <link rel="canonical" href={`${canonicalURL}/photos`} />
        {alternate?.map(({ locale, href }) => (
          <link key={locale} rel="alternate" href={`${href}/photos`} hrefLang={locale} />
        ))}
      </Head>
      <main className="overflow-hidden">
        <GalleryOverlay
          goToListing={goToListing}
          headerComponent={<GalleryHeaderContainer />}
          categoriesComponent={<GalleryCategoriesContainer />}>
          <GalleryListContainer />
        </GalleryOverlay>
      </main>
    </>
  );
};

export default ListingPhotosIndex;
